import { EERIE, GYPSUM } from 'HubStyleTokens/colors';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import UICloseButton from 'UIComponents/button/UICloseButton';
import UIFormControl from 'UIComponents/form/UIFormControl';
import UIStepperInput from 'UIComponents/input/UIStepperInput';
import UIFlex from 'UIComponents/layout/UIFlex';
import { API_CALLS_DAILY } from 'pricing-pages-lib/constants/LimitIds';
import { SEATS_VERSIONS_FEATURE_FLAG } from 'pricing-pages-lib/constants/PublicFeatureFlags';
import { SEATS_ACCESS } from 'pricing-pages-lib/constants/Scopes';
import getUsageTracker from 'pricing-pages-lib/utils/getUsageTracker';
import { createProductConfig } from 'pricing-pages-lib/utils/productConfigUtils';
import Screens from 'pricing-pages-ui/constants/routing/Screens';
import Raven from 'raven-js';
import { useContext } from 'react';
import { useSelector } from 'react-redux';
import { API_LIMIT_INCREASE } from 'self-service-api/constants/ApiNames';
import { RECURRING } from 'self-service-api/constants/ChargeTypes';
import { PRODUCT_VERSION, QUANTITY_PACK } from 'self-service-api/constants/SkuTypes';
import { CONTACT, MARKETING_CONTACT_DEPRECATED, USER } from 'self-service-api/constants/UnitTypes';
import styled from 'styled-components';
import { PlanBuilderContext } from '../../../context/PlanBuilderContext';
import { useAppSelector } from '../../../reducers';
import { isAppAuthSelector } from '../../../selectors/AuthSelectors';
import { purchasedContactsQuantitySelector } from '../../../selectors/ContactsSelectors';
import { countrySelector } from '../../../selectors/CountrySelectors';
import { activeCurrencySelector } from '../../../selectors/CurrencySelectors';
import { isUngatedForSeatsSelector, isUngatedSelector } from '../../../selectors/GatingSelectors';
import { calculatedPriceForProductConfigurationsInListSelector, planAdditionalCostsForProductConfigurationInListSelector } from '../../../selectors/PricingSelectors';
import { productsFromProductInventoryQuantitySelector } from '../../../selectors/ProductInventorySelectors';
import { billingNameKeySelector, includedCoreSeatsQuantityForProductVersionSelector, isCoreSeatSkuSelector, isPaidCoreSeatSkuSelector, isQuantifiableHubSelector, isSalesOrServiceProOrEntSkuSelector, maxProductVersionQuantitySelector, productForSkuIdSelector, skuIdHasBundledProductsSelector, skuIdIsHubSelector, skuIdIsQuantityPackSelector, skuSelector } from '../../../selectors/ProductsSelectors';
import PriceDisplay from '../../common/pricing/PriceDisplay';
import { BundleIncludesDisplay } from './BundleIncludesDisplay';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const ShoppingCartItemContainer = styled.div.withConfig({
  displayName: "ShoppingCartItem__ShoppingCartItemContainer",
  componentId: "agnomc-0"
})(["background-color:", ";padding:16px 24px;position:relative;&:not(:last-child){margin-bottom:8px;}"], GYPSUM);
export const PlanTitle = styled.div.withConfig({
  displayName: "ShoppingCartItem__PlanTitle",
  componentId: "agnomc-1"
})(["font-weight:600;"]);
const CloseButton = styled(UICloseButton).withConfig({
  displayName: "ShoppingCartItem__CloseButton",
  componentId: "agnomc-2"
})(["position:absolute;top:10px;right:10px;"]);
const ShoppingCartUnit = ({
  skuId,
  quantity,
  inline = true
}) => {
  const sku = useAppSelector(state => skuSelector(state, {
    skuId
  }));
  let billingNameKey = useAppSelector(state => billingNameKeySelector(state, {
    skuId
  }));
  let message;
  if (sku.skuType === QUANTITY_PACK) {
    const {
      quantityUnit: {
        unitType
      }
    } = sku;
    message = /*#__PURE__*/_jsx(FormattedMessage, {
      message: unitType === MARKETING_CONTACT_DEPRECATED || unitType === CONTACT ? 'planBuilder.shoppingCart.additionalContacts' : quantity === 1 ? 'planBuilder.shoppingCart.additionalUsers.one' : 'planBuilder.shoppingCart.additionalUsers.other',
      options: {
        count: quantity
      }
    });
  } else {
    if (billingNameKey === 'brand-domain') {
      billingNameKey = 'brand-domain-new';
    }
    message = /*#__PURE__*/_jsx(FormattedMessage, {
      message: `planBuilder.shoppingCart.addonsAndServices.${billingNameKey}`,
      options: {
        count: quantity
      }
    });
  }
  return inline ? message : /*#__PURE__*/_jsx("div", {
    children: message
  });
};
const ShoppingCartItem = ({
  skuId,
  allProductConfigurations,
  onRemove,
  isCloseable = true,
  shouldDisplayPrice = true,
  queryParamTerm,
  priceOverride
}) => {
  const {
    screen,
    setProductConfigurations
  } = useContext(PlanBuilderContext);
  const productConfiguration = allProductConfigurations.find(({
    skuId: searchSkuId
  }) => skuId === searchSkuId);
  const skuIdIsHub = useAppSelector(state => skuIdIsHubSelector(state, {
    skuId
  }));
  const isQuantifiableHub = useAppSelector(state => isQuantifiableHubSelector(state, {
    skuId
  }));
  const skuIdHasBundle = useAppSelector(state => skuIdHasBundledProductsSelector(state, {
    skuId
  }));
  const skuIdIsFreeCoreSeat = useAppSelector(state => {
    const isCoreSeat = isCoreSeatSkuSelector(state, {
      skuId
    });
    const isPaidCoreSeat = isPaidCoreSeatSkuSelector(state, {
      skuId
    });
    return isCoreSeat && !isPaidCoreSeat;
  });
  const isUngatedForSeats = useSelector(isUngatedForSeatsSelector);
  const country = useAppSelector(countrySelector);
  const isAppAuth = useAppSelector(isAppAuthSelector);
  const seatsPublicFlag = useAppSelector(state => isUngatedSelector(state, {
    gateKey: SEATS_VERSIONS_FEATURE_FLAG
  }));
  const seatsScope = useAppSelector(state => isUngatedSelector(state, {
    gateKey: SEATS_ACCESS
  }));
  const unitsProductConfigurations = useAppSelector(state => {
    const sku = skuSelector(state, {
      skuId
    });
    const {
      skuType,
      quantifiable
    } = sku;

    // If the whole line item is just for one quantity pack or addon
    if (!skuIdIsHub) {
      if (isUngatedForSeats) {
        return [];
      }
      if (isCoreSeatSkuSelector(state, {
        skuId
      })) {
        Raven.captureMessage('Attempting to show core seat sku after seats gate check', {
          extra: {
            skuId,
            isAppAuth,
            isUngatedForSeats,
            seatsPublicFlag,
            seatsScope,
            country,
            currentUrl: window.location.href
          }
        });
      }
      if (skuType === PRODUCT_VERSION) {
        const maxMerchQuantity = maxProductVersionQuantitySelector(state, {
          skuId
        });
        const isQuantifiable = quantifiable && maxMerchQuantity > 1;
        let unitQuantityToUse;
        unitQuantityToUse = isQuantifiable ? productConfiguration.quantity - productsFromProductInventoryQuantitySelector(state, {
          skuId
        }) : null;
        const {
          apiName
        } = productForSkuIdSelector(state, {
          skuId
        });
        if (apiName === API_LIMIT_INCREASE) {
          // Todo need backend to supply ProductVersion limits
          const limits = skuSelector(state, {
            skuId
          }).skuLimits;
          const dailyLimit = limits.find(limit => limit.name === API_CALLS_DAILY);
          if (!dailyLimit) return [];
          unitQuantityToUse = dailyLimit.limitAmount;
        }
        if (unitQuantityToUse === null) return [];
        return [createProductConfig({
          skuId,
          quantity: unitQuantityToUse
        })];
      } else if (skuType === QUANTITY_PACK) {
        const {
          quantityUnit: {
            unitType
          }
        } = sku;
        let quantityAdjustedForOwnership = productConfiguration.quantity;
        if (unitType === MARKETING_CONTACT_DEPRECATED || unitType === CONTACT) {
          quantityAdjustedForOwnership -= purchasedContactsQuantitySelector(state, {
            skuIdForMCCheck: skuId
          });
        } else if (unitType === USER) {
          quantityAdjustedForOwnership -= productsFromProductInventoryQuantitySelector(state, {
            skuId
          });
        }
        return [createProductConfig({
          skuId: productConfiguration.skuId,
          quantity: quantityAdjustedForOwnership
        })];
      }
      return [productConfiguration];
    }
    const {
      quantityPackDetails
    } = sku;
    return quantityPackDetails.map(({
      skuId: quantityPackSkuId,
      quantityUnit: {
        includedWithProductVersion
      }
    }) => {
      let quantityToUse = includedWithProductVersion;
      if (!isUngatedForSeats) {
        const cartConfig = allProductConfigurations.find(({
          skuId: searchSkuId
        }) => searchSkuId === quantityPackSkuId);
        if (cartConfig) quantityToUse += cartConfig.quantity;
      }
      if (isCoreSeatSkuSelector(state, {
        skuId: quantityPackSkuId
      })) {
        Raven.captureMessage('Attempting to show core seat sku as quantityPack', {
          extra: {
            skuId,
            quantityPackSkuId,
            isAppAuth,
            isUngatedForSeats,
            seatsPublicFlag,
            seatsScope,
            country,
            currentUrl: window.location.href
          }
        });
      }
      return createProductConfig({
        skuId: quantityPackSkuId,
        quantity: quantityToUse
      });
    });
  });
  const includedCoreSeatsQuantity = useAppSelector(state => {
    const quantity = includedCoreSeatsQuantityForProductVersionSelector(state, {
      productVersionSkuId: skuId
    });
    return skuIdIsQuantityPackSelector(state, {
      skuId
    }) ? 0 : quantity;
  });
  const unitsProductConfigurationsForPriceDisplay = useAppSelector(state => allProductConfigurations.filter(({
    skuId: searchSkuId
  }) => {
    const sku = skuSelector(state, {
      skuId: searchSkuId
    });
    return sku.skuType === QUANTITY_PACK && sku.productVersionSkuId === skuId;
  }));
  const activeCurrencyCode = useSelector(activeCurrencySelector);
  const priceCalculationProductConfigurations = isUngatedForSeats ? [productConfiguration] : [productConfiguration, ...unitsProductConfigurationsForPriceDisplay];
  const priceObject = useAppSelector(state => priceOverride || (skuIdIsHub && !isQuantifiableHub ? calculatedPriceForProductConfigurationsInListSelector(state, {
    allProductConfigurations,
    productConfigurations: priceCalculationProductConfigurations,
    queryParamTerm
  }) : planAdditionalCostsForProductConfigurationInListSelector(state, {
    allProductConfigurations,
    productConfigurations: priceCalculationProductConfigurations,
    queryParamTerm
  })));
  const isRecurring = useAppSelector(state => productForSkuIdSelector(state, {
    skuId
  }).chargeType === RECURRING);
  const isSalesServiceSkuAtProOrEnt = useAppSelector(state => isSalesOrServiceProOrEntSkuSelector(state, {
    skuId
  }));
  const ownedQuantity = useAppSelector(state => productsFromProductInventoryQuantitySelector(state, {
    skuId
  }));
  const price = isRecurring ? priceObject.monthly.calculatedPrice : priceObject.oneTimeFee.calculatedPrice;
  const billingNameKey = useAppSelector(state => billingNameKeySelector(state, {
    skuId
  }));
  const {
    apiName
  } = useAppSelector(state => productForSkuIdSelector(state, {
    skuId
  }));
  const handleRemove = () => {
    getUsageTracker().track('planBuilderInteraction', {
      component: 'ShoppingCartItem',
      action: 'removed item',
      skuId,
      productName: apiName,
      screen,
      currency: activeCurrencyCode
    });
    onRemove();
  };
  const handleUpdateQuantity = event => {
    const newValue = parseInt(event.target.value, 10);
    setProductConfigurations(prevConfigs => {
      const filteredConfigs = prevConfigs.filter(({
        skuId: configSkuId
      }) => {
        return skuId !== configSkuId;
      });
      if (newValue > 0) {
        filteredConfigs.push(createProductConfig({
          skuId,
          quantity: newValue
        }));
      }
      return filteredConfigs;
    });
  };
  if (isUngatedForSeats) {
    const adjustedQuantityForOwnership = productConfiguration.quantity - ownedQuantity;
    return /*#__PURE__*/_jsxs(ShoppingCartItemContainer, {
      "data-selenium-class": "shopping-cart-item-container",
      children: [/*#__PURE__*/_jsxs(UIFlex, {
        direction: "row",
        justify: "between",
        children: [/*#__PURE__*/_jsx(PlanTitle, {
          className: "m-right-6",
          children: (skuIdIsHub || skuIdHasBundle) && !isSalesServiceSkuAtProOrEnt ? /*#__PURE__*/_jsx(FormattedMessage, {
            "data-selenium-class": "shopping-cart-item-title",
            message: `pricing-pages.products.${billingNameKey}.title`
          }) : /*#__PURE__*/_jsx(FormattedMessage, {
            "data-selenium-class": "shopping-cart-item-title",
            message: `pricing-pages.products.${billingNameKey}.priceBreakdownTitle`,
            options: {
              count: adjustedQuantityForOwnership
            }
          })
        }), /*#__PURE__*/_jsxs(UIFlex, {
          className: "align-end",
          direction: "column",
          children: [isSalesServiceSkuAtProOrEnt && screen !== Screens.INTERSTITIAL && /*#__PURE__*/_jsx(UIFormControl, {
            style: {
              marginLeft: '10px',
              marginRight: '10px'
            },
            children: /*#__PURE__*/_jsx(UIStepperInput, {
              value: productConfiguration.quantity,
              min: Math.max(1, ownedQuantity),
              onChange: handleUpdateQuantity,
              style: {
                minWidth: '84px',
                maxWidth: '100px',
                fontSize: 12,
                height: 34
              }
            })
          }), shouldDisplayPrice && /*#__PURE__*/_jsxs("span", {
            className: "m-x-2",
            children: [/*#__PURE__*/_jsx(PriceDisplay, {
              className: "text",
              price: price,
              currencyCode: activeCurrencyCode
            }), isRecurring && /*#__PURE__*/_jsx(FormattedMessage, {
              message: "planBuilder.priceDisplay.perMonth"
            })]
          })]
        })]
      }), /*#__PURE__*/_jsxs("div", {
        children: [skuIdIsFreeCoreSeat && /*#__PURE__*/_jsx(FormattedReactMessage, {
          message: `pricing-pages.products.${billingNameKey}.includes`
        }), includedCoreSeatsQuantity > 0 && /*#__PURE__*/_jsx("div", {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "planBuilder.includedCoreSeats",
            options: {
              count: includedCoreSeatsQuantity
            }
          })
        }), unitsProductConfigurations.map(({
          skuId: quantityPackSkuId,
          quantity
        }) => /*#__PURE__*/_jsx(ShoppingCartUnit, {
          skuId: quantityPackSkuId,
          quantity: quantity,
          inline: unitsProductConfigurations.length === 1
        }, quantityPackSkuId)), /*#__PURE__*/_jsx(BundleIncludesDisplay, {
          skuId: skuId
        })]
      }), isCloseable && /*#__PURE__*/_jsx(CloseButton, {
        color: EERIE,
        onClick: handleRemove,
        size: "medium",
        "data-selenium-class": "shopping-cart-item-remove"
      })]
    });
  }
  return /*#__PURE__*/_jsxs(ShoppingCartItemContainer, {
    "data-selenium-class": "shopping-cart-item-container",
    children: [/*#__PURE__*/_jsx(PlanTitle, {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        "data-selenium-class": "shopping-cart-item-title",
        message: `pricing-pages.products.${billingNameKey}.title`
      })
    }), /*#__PURE__*/_jsx("div", {
      children: shouldDisplayPrice && /*#__PURE__*/_jsxs("span", {
        style: {
          fontWeight: 600
        },
        children: [/*#__PURE__*/_jsx(PriceDisplay, {
          className: "text",
          price: price,
          currencyCode: activeCurrencyCode
        }), isRecurring && /*#__PURE__*/_jsx(FormattedMessage, {
          message: "planBuilder.priceDisplay.perMonth"
        })]
      })
    }), /*#__PURE__*/_jsx("div", {
      children: unitsProductConfigurations.map(({
        skuId: quantityPackSkuId,
        quantity
      }) => /*#__PURE__*/_jsx(ShoppingCartUnit, {
        skuId: quantityPackSkuId,
        quantity: quantity,
        inline: unitsProductConfigurations.length === 1
      }, quantityPackSkuId))
    }), isCloseable && /*#__PURE__*/_jsx(CloseButton, {
      color: EERIE,
      onClick: handleRemove,
      size: "medium",
      "data-selenium-class": "shopping-cart-item-remove"
    })]
  });
};
export default ShoppingCartItem;